import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { shareSavedView, unshareSavedView } from "../../../../api/t2tSavedViewsApi";
import { getData } from "../../../../FetchApiServices";

export default function ShareViewModal({ show, onClose, view, onUpdated }) {
  const [shareType, setShareType] = useState("team");
  const [canEdit, setCanEdit]     = useState(false);
  const [users, setUsers]         = useState([]);
  const [roles, setRoles]         = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState("");

  useEffect(() => {
    if (!show) return;
    setShareType(view?.share_type || "team");
    setSelectedIds([]);
    setError("");
  }, [show, view]);

  useEffect(() => {
    if (!show) return;
    if (shareType === "specific_users") {
      getData("t2t/users?limit=200").then(r => setUsers(r?.data || [])).catch(() => {});
    }
    if (shareType === "specific_roles") {
      getData("t2t/roles").then(r => setRoles(r?.data || [])).catch(() => {});
    }
  }, [show, shareType]);

  const toggleId = id =>
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleShare = async () => {
    setSaving(true); setError("");
    try {
      const body = { share_type: shareType, target_ids: selectedIds, can_edit: canEdit ? 1 : 0 };
      const result = await shareSavedView(view.id, body);
      onUpdated(result.data);
      onClose();
    } catch (e) {
      setError(e.message || "Share failed");
    } finally { setSaving(false); }
  };

  const handleUnshare = async (shareId) => {
    try {
      await unshareSavedView(view.id, shareId);
      onUpdated({ ...view, shares: (view.shares || []).filter(s => s.id !== shareId) });
    } catch (e) { setError(e.message); }
  };

  if (!show || !view) return null;

  const showTargets = shareType === "specific_users" || shareType === "specific_roles";
  const items = shareType === "specific_users" ? users : roles;
  const labelKey = shareType === "specific_users" ? "name" : "role_name";
  const idKey = shareType === "specific_users" ? "user_id" : "id";

  return (
    <div className="modal d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,0.45)" }}>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 480 }}>
        <div className="modal-content">
          <div className="modal-header py-3 px-4">
            <h6 className="modal-title fw-semibold mb-0">
              Share &ldquo;{view.name}&rdquo;
            </h6>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body px-4 py-3 d-flex flex-column gap-3">
            {error && <div className="alert alert-danger py-2 mb-0" style={{ fontSize: 13 }}>{error}</div>}

            <div>
              <label className="form-label small fw-semibold">Share with</label>
              <select
                className="form-select form-select-sm"
                value={shareType}
                onChange={e => setShareType(e.target.value)}
              >
                <option value="private">Private (only me)</option>
                <option value="team">Everyone in the team</option>
                <option value="specific_users">Specific users</option>
                <option value="specific_roles">Specific roles</option>
              </select>
            </div>

            {showTargets && (
              <div>
                <label className="form-label small fw-semibold">
                  Select {shareType === "specific_users" ? "users" : "roles"}
                </label>
                <div className="border rounded p-2" style={{ maxHeight: 200, overflowY: "auto" }}>
                  {items.length === 0
                    ? <p className="text-muted small mb-0">Loading…</p>
                    : items.map(item => (
                      <div key={item[idKey]} className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`share-${item[idKey]}`}
                          checked={selectedIds.includes(item[idKey])}
                          onChange={() => toggleId(item[idKey])}
                        />
                        <label className="form-check-label small" htmlFor={`share-${item[idKey]}`}>
                          {item[labelKey]}
                        </label>
                      </div>
                    ))
                  }
                </div>
              </div>
            )}

            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="can-edit-check"
                checked={canEdit}
                onChange={e => setCanEdit(e.target.checked)}
              />
              <label className="form-check-label small" htmlFor="can-edit-check">
                Allow shared users to edit this view
              </label>
            </div>

            {/* Existing shares */}
            {view.shares?.length > 0 && (
              <div>
                <div className="fw-semibold small text-muted mb-1">Current shares</div>
                {view.shares.map(s => (
                  <div key={s.id} className="d-flex align-items-center justify-content-between py-1 border-bottom" style={{ fontSize: 13 }}>
                    <span>{s.shared_with === "user" ? "User" : "Role"} #{s.target_id}</span>
                    <button
                      className="btn btn-link btn-sm text-danger p-0"
                      onClick={() => handleUnshare(s.id)}
                    >Remove</button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="modal-footer py-2 px-4">
            <button className="btn btn-sm btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button className="btn btn-sm btn-primary" onClick={handleShare} disabled={saving}>
              {saving ? <span className="spinner-border spinner-border-sm me-1" /> : null}
              Share
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

ShareViewModal.propTypes = {
  show:      PropTypes.bool.isRequired,
  onClose:   PropTypes.func.isRequired,
  view:      PropTypes.object,
  onUpdated: PropTypes.func.isRequired,
};
